import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { InfoBar } from './InfoBar';
import { LeftSidebar } from './LeftSidebar';
import { RightSidebar } from './RightSidebar';
import { Footer } from './Footer';
import { AppTabBar } from './AppTabBar';
import { SubMenuBar } from './SubMenuBar';
import { PageRenderer, getPageByPath } from './PageRegistry';
import { useAppTabsStore } from '@/stores/appTabsStore';

interface MainLayoutProps {
  showRightSidebar?: boolean;
  rightSidebarTitle?: string;
  rightSidebarContent?: React.ReactNode;
}

export function MainLayout({
  showRightSidebar = false,
  rightSidebarTitle,
  rightSidebarContent
}: MainLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(showRightSidebar);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { tabs, activeTabId, openTab, switchTab } = useAppTabsStore();

  // Sync URL dengan tab saat pertama load atau URL berubah langsung (misalnya dari bookmark)
  useEffect(() => {
    const currentPath = location.pathname;
    const page = getPageByPath(currentPath);

    if (page) {
      // Cek apakah tab sudah terbuka
      const existingTab = tabs.find((t) => t.id === page.id);

      if (existingTab) {
        // Tab sudah ada, switch ke tab tersebut jika belum aktif
        if (activeTabId !== existingTab.id) {
          switchTab(existingTab.id);
        }
      } else {
        // Tab belum ada, buka tab baru
        openTab({
          id: page.id,
          label: page.label,
          path: page.path,
          icon: page.icon,
        });
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync tab aktif dengan URL
  useEffect(() => {
    if (activeTabId) {
      const activeTab = tabs.find((t) => t.id === activeTabId);
      if (activeTab && activeTab.path !== location.pathname) {
        navigate(activeTab.path);
      }
    }
  }, [activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSidebar = () => {
    // On mobile, toggle mobile menu
    if (window.innerWidth <= 768) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      // On desktop, collapse/expand sidebar
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const closeRightSidebar = () => {
    setRightSidebarOpen(false);
  };

  const contentClasses = [
    'content-wrapper',
    sidebarCollapsed ? 'sidebar-collapsed' : '',
    !rightSidebarOpen ? 'no-sidebar-right' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="app-wrapper">
      <Header onToggleSidebar={toggleSidebar} />
      <InfoBar />

      <div className="app-body">
        <LeftSidebar collapsed={sidebarCollapsed} />

        {/* Mobile overlay */}
        <div
          className={`sidebar-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        <main className={contentClasses}>
          {/* SubMenuBar - muncul saat user klik menu dengan children */}
          <SubMenuBar />

          {/* App Tab Bar */}
          <AppTabBar />

          {/* Tab Contents - render semua tab untuk preserve state */}
          {tabs.length === 0 ? (
            // Tidak ada tab terbuka - tampilkan pesan kosong
            <div className="empty-workspace" style={{ padding: '60px 20px', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Pilih menu di sebelah kiri untuk memulai
              </p>
            </div>
          ) : (
            // Render semua tab yang terbuka (hidden jika tidak aktif)
            tabs.map((tab) => (
              <PageRenderer
                key={tab.id}
                pageId={tab.id}
                isActive={tab.id === activeTabId}
              />
            ))
          )}
        </main>

        <RightSidebar
          collapsed={!rightSidebarOpen}
          onClose={closeRightSidebar}
          title={rightSidebarTitle}
        >
          {rightSidebarContent}
        </RightSidebar>
      </div>

      <Footer sidebarCollapsed={sidebarCollapsed} />
    </div>
  );
}
